import type { IndexUiState, UiState } from 'instantsearch.js'
import { useMemo } from 'react'
import {
  Configure,
  InstantSearch,
  type InstantSearchProps
} from 'react-instantsearch'
import {
  type IndexName,
  useConfigContext,
  type WidgetConfig
} from './ConfigContext'
import SearchUI from './SearchUI'
import { WP_LOCALE } from '../../../../assets/utils/algolia/algoliaLocalization'
import createSearchClient from '../../../../assets/utils/algolia/createSearchClient'
import { history } from 'instantsearch.js/es/lib/routers'

const instantSearchProps = {
  future: {
    preserveSharedStateOnUnmount: true
  },
  insights: false
} as const satisfies Partial<InstantSearchProps>

type TUiState = {
  [key in IndexName]: IndexUiState
}

export type AppProps<TRouteState = TUiState> = {
  routing?: InstantSearchProps<TUiState, TRouteState>['routing']
}

function getRouting(indexName: IndexName): AppProps<{
  q?: string
  group?: string
  lang?: string
  type?: string
}>['routing'] {
  return {
    router: history({
      cleanUrlOnDispose: true,
      parseURL({ qsModule, location }) {
        return qsModule.parse(location.search.slice(1))
      },
      createURL({ qsModule, location, routeState }) {
        const { origin, pathname, hash } = location
        const queriesFromUrl = qsModule.parse(location.search.slice(1))
        const restQueries = Object.fromEntries(
          Object.entries(queriesFromUrl).filter(
            ([key]) => !Object.keys(routeState).includes(key)
          )
        )
        const queryString = qsModule.stringify(
          { ...routeState, ...restQueries },
          { addQueryPrefix: true, arrayFormat: 'repeat' }
        )

        return `${origin}${pathname}${queryString}${hash}`
      }
    }),
    stateMapping: {
      stateToRoute(uiState) {
        const indexState = uiState[indexName]
        return {
          q: indexState?.query,
          group: indexState?.menu?.[`groups.title.${WP_LOCALE}`],
          lang: indexState?.menu?.[`languages.fullName.${WP_LOCALE}`],
          type:
            indexState?.menu?.isPermanent === 'true'
              ? 'offers'
              : indexState?.menu?.isPermanent === 'false'
                ? 'events'
                : undefined
        }
      },
      routeToState(routeState) {
        return {
          [indexName]: {
            query: routeState.q,
            menu: {
              [`groups.title.${WP_LOCALE}`]: routeState.group,
              [`languages.fullName.${WP_LOCALE}`]: routeState.lang,
              isPermanent:
                routeState.type === 'offers'
                  ? 'true'
                  : routeState.type === 'events'
                    ? 'false'
                    : undefined
            }
          }
        } as UiState
      }
    }
  }
}

function createTypeFilterString(typePreFilter: WidgetConfig['typePreFilter']) {
  const value =
    typePreFilter === 'offers'
      ? 'true'
      : typePreFilter === 'events'
        ? 'false'
        : undefined
  return value ? `isPermanent:${value}` : value
}

export default function App() {
  const { appId, apiKey, postsPerPage, indexName, typePreFilter } =
    useConfigContext()
  const searchClient = useMemo(() => {
    return createSearchClient(appId, apiKey)
  }, [appId, apiKey])

  const routing = getRouting(indexName)

  return (
    <InstantSearch
      {...instantSearchProps}
      indexName={indexName}
      routing={routing}
      searchClient={searchClient}
    >
      <Configure
        hitsPerPage={postsPerPage}
        getRankingInfo
        filters={createTypeFilterString(typePreFilter)}
      />
      <SearchUI />
    </InstantSearch>
  )
}
